import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Subject } from 'rxjs';

import { WebSocketService } from '@core/websocket/websocket.service';
import { WsProtobufService } from '@core/websocket/ws-protobuf.service';
import { SessionService } from '@core/services/session.service';
import { ImageService } from '@core/services/image.service';
import {
  ActiveDriverRequest,
  ActiveVehicleWashing,
  DriverReceiveWorkingShiftEvent,
  DriverStateEvent,
  SessionType,
  WashingType,
} from '@core/websocket/websocket.models';
import { EventId, RequestPath, ResponseStatus } from '@core/websocket/websocket.constants';

export type WashStep =
  | 'form'           // Форма: адреса + тип + фото авто
  | 'pending'        // Запит надіслано, очікуємо підтвердження
  | 'approved'       // Підтверджено, готово до початку
  | 'active'         // Мийка в процесі (таймер)
  | 'rejected'       // Запит відхилено
  | 'cancel-pending'; // Скасування підтвердженого запиту надіслано

@Injectable({ providedIn: 'root' })
export class WashService implements OnDestroy {
  private readonly ws = inject(WebSocketService);
  private readonly proto = inject(WsProtobufService);
  private readonly sessionService = inject(SessionService);
  private readonly imageService = inject(ImageService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── State signals ────────────────────────────────────────────────────────

  private readonly _step = signal<WashStep>('form');
  readonly step = this._step.asReadonly();

  private readonly _addressName = signal('');
  readonly addressName = this._addressName.asReadonly();

  private readonly _selectedType = signal<WashingType | null>(WashingType.Stationary);
  readonly selectedType = this._selectedType.asReadonly();

  private readonly _activeRequestId = signal<string | null>(null);
  readonly activeRequestId = this._activeRequestId.asReadonly();

  /** Ідентифікатор активної мийки (після StartVehicleWashing) */
  private readonly _washingId = signal<string | null>(null);
  readonly washingId = this._washingId.asReadonly();

  /** Тип мийки, що зберігається після відправки запиту (для routing finish/prestart) */
  private readonly _washingType = signal<WashingType | null>(null);
  readonly washingType = this._washingType.asReadonly();

  private readonly _elapsedSeconds = signal(0);
  readonly elapsedSeconds = this._elapsedSeconds.asReadonly();

  private readonly _cancelRequestId = signal<string | null>(null);

  /** ID фото авто, завантаженого для поточного запиту на мийку */
  private readonly _carPhotoImageId = signal<string | null>(null);
  readonly carPhotoImageId = this._carPhotoImageId.asReadonly();

  /** ID фото табло мийки (для prestart самообслуговування) */
  private readonly _displayPhotoImageId = signal<string | null>(null);
  readonly displayPhotoImageId = this._displayPhotoImageId.asReadonly();

  /** Фото для сторінки завершення мийки */
  private readonly _finishReceiptImageId = signal<string | null>(null);
  readonly finishReceiptImageId = this._finishReceiptImageId.asReadonly();

  private readonly _finishCarImageId = signal<string | null>(null);
  readonly finishCarImageId = this._finishCarImageId.asReadonly();

  private readonly _finishInteriorImageId = signal<string | null>(null);
  readonly finishInteriorImageId = this._finishInteriorImageId.asReadonly();

  /**
   * ID запитів/мийок, скасованих у цій сесії.
   * Потрібно щоб ігнорувати їх у init() якщо DriverStateEvent ще застарілий.
   */
  private readonly _cancelledIds = new Set<string>();

  private timerInterval: ReturnType<typeof setInterval> | null = null;

  readonly formattedTime = computed(() => {
    const s = this._elapsedSeconds();
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = (s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  });

  /** Базова валідація форми (без фото — фото перевіряється у компоненті) */
  readonly canSendBase = computed(() =>
    !!this._addressName().trim() && this._selectedType() !== null,
  );

  /** Емітує коли оператор підтвердив запит на скасування підтвердженого запиту */
  readonly cancelApprovedConfirmed$ = new Subject<void>();

  /**
   * Стріляє лише при відхиленні через реальний WS-івент (DriverRequestRejected).
   * Не стріляє при ініціалізації з lastDriverState/workingShiftData.
   */
  private readonly _rejectedViaWsEvent$ = new Subject<void>();
  readonly rejectedViaWsEvent$ = this._rejectedViaWsEvent$.asObservable();

  // ─── Constructor ──────────────────────────────────────────────────────────

  constructor() {
    // DriverRequestConfirmed → 'approved' або підтвердження скасування
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestConfirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestConfirmedEvent(e.payload);
        if (this._step() === 'pending' && event.id === this._activeRequestId()) {
          this._step.set('approved');
        } else if (this._step() === 'cancel-pending' && event.id === this._cancelRequestId()) {
          this.resetState();
          this.cancelApprovedConfirmed$.next();
        }
      });

    // DriverRequestRejected → 'rejected'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestRejected),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestEvent(e.payload);
        if (this._step() === 'pending' && event.id === this._activeRequestId()) {
          this._step.set('rejected');
          this._rejectedViaWsEvent$.next();
        }
      });

    // DriverStateEvent → синхронізація
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverStateEvent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        this.handleDriverStateEvent(this.proto.decodeDriverStateEvent(e.payload));
      });

    // VehicleWashingStarted → 'active'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.VehicleWashingStarted),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (!e.payload?.length) return;
        const event = this.proto.decodeVehicleWashingEvent(e.payload);
        this._washingId.set(event.id);
        this._step.set('active');
        this.startElapsedTimer(Math.floor(Date.now() / 1000));
      });

    // VehicleWashingFinished / Canceled → reset
    this.ws.events$
      .pipe(
        filter((e) =>
          e.id === EventId.VehicleWashingFinished || e.id === EventId.VehicleWashingCanceled,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.stopTimer();
        this.resetState();
      });
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /** Ініціалізація при відкритті wash-page */
  init(): void {
    if (this._step() !== 'form') return;

    const liveState = this.sessionService.lastDriverState();
    if (liveState) {
      this.initFromDriverStateEvent(liveState);
      return;
    }

    const wshData = this.sessionService.workingShiftData();
    if (wshData) {
      this.initFromWorkingShiftData(wshData);
    }
  }

  setAddress(address: string): void {
    this._addressName.set(address);
  }

  setCarPhotoImageId(id: string | null): void {
    this._carPhotoImageId.set(id);
  }

  setDisplayPhotoImageId(id: string | null): void {
    this._displayPhotoImageId.set(id);
  }

  setFinishReceiptImageId(id: string | null): void {
    this._finishReceiptImageId.set(id);
  }

  setFinishCarImageId(id: string | null): void {
    this._finishCarImageId.set(id);
  }

  setFinishInteriorImageId(id: string | null): void {
    this._finishInteriorImageId.set(id);
  }

  selectType(type: WashingType): void {
    this._selectedType.set(type);
  }

  /** Надіслати запит на мийку. sessionId читається з SessionService автоматично. */
  async sendRequest(): Promise<void> {
    const selectedType = this._selectedType();
    const wshData = this.sessionService.workingShiftData();
    if ((!selectedType && selectedType != 0) || !wshData) return;

    const sessionId = this.sessionService.sessionId() ?? '';
    const payload = this.proto.encodeCreateVehicleWashingPayload(
      this._addressName().trim(),
      selectedType,
    );
    const body = this.proto.encodeCreateDriverRequest(
      wshData.workingShift.id,
      SessionType.StartVehicleWashing,
      sessionId,
      payload,
    );

    const response = await this.ws.sendRequest(RequestPath.CreateDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`CreateDriverRequest failed: ${response.code}`);
    }

    if (response.body?.length) {
      const parsed = this.proto.decodeCreateDriverRequestResponse(response.body);
      this._activeRequestId.set(parsed.id);
    }

    this._washingType.set(this._selectedType());
    this._step.set('pending');
  }

  /** Скасувати запит (зі стану pending).
   *  Порядок: очистити локальне фото → скасувати запит → очистити кеш сесії.
   *
   *  ВАЖЛИВО: сервер "споживає" сесію StartVehicleWashing ще при обробці
   *  CreateDriverRequest, тому DeleteImage після цього завжди повертає
   *  "Can't find active driver session". Сервер сам очищає фото при cancel.
   *  Клієнт лише скидає локальне посилання.
   *
   *  resetState() навмисно не викликається — компонент робить це ПІСЛЯ навігації,
   *  щоб уникнути короткого відображення форми.
   */
  async cancelRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    // Очищаємо локальне посилання на фото. Не надсилаємо DeleteImage —
    // сесія вже недійсна після CreateDriverRequest, запит завжди падає.
    // Сервер видаляє фото самостійно при обробці CancelDriverRequest.
    this._carPhotoImageId.set(null);

    // Скасовуємо запит
    this._cancelledIds.add(requestId);
    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.CancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(requestId);
      throw new Error(`CancelDriverRequest failed: ${response.code}`);
    }

    // Сервер видаляє сесію при cancel — очищаємо лише клієнтський кеш
    this.sessionService.clearSessionCacheByType(SessionType.StartVehicleWashing);
    // resetState() — викликається компонентом після навігації
  }

  /** Запросити скасування підтвердженого запиту (зі стану approved) */
  async cancelApprovedRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    this._cancelledIds.add(requestId);
    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.RequestCancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(requestId);
      throw new Error(`RequestCancelApprovedRequest failed: ${response.code}`);
    }

    if (response.body?.length) {
      const cancelId = this.proto.decodeCancelRequestResponse(response.body);
      if (cancelId) this._cancelRequestId.set(cancelId);
    }

    this._step.set('cancel-pending');
  }

  /**
   * Розпочати мийку (StartVehicleWashing).
   * Для самообслуговування передається preStartSessionId.
   * Після виклику step → 'active', washingId встановлюється.
   */
  async startWashing(preStartSessionId?: string): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    const body = this.proto.encodeStartVehicleWashing(requestId, preStartSessionId);
    const response = await this.ws.sendRequest(RequestPath.StartVehicleWashing, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`StartVehicleWashing failed: ${response.code}`);
    }

    if (response.body?.length) {
      const parsed = this.proto.decodeStartVehicleWashingResponse(response.body);
      this._washingId.set(parsed.id);
    }

    this._step.set('active');
    this.startElapsedTimer(Math.floor(Date.now() / 1000));
  }

  /**
   * Завершити мийку (FinishVehicleWashing).
   * sessionId і amount передаються з WashFinishPageComponent.
   */
  async finishWashing(sessionId: string, amount: number): Promise<void> {
    const washingId = this._washingId();
    if (!washingId) return;

    const body = this.proto.encodeFinishVehicleWashing(washingId, sessionId, amount);
    const response = await this.ws.sendRequest(RequestPath.FinishVehicleWashing, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`FinishVehicleWashing failed: ${response.code}`);
    }
    // Додаємо washingId до _cancelledIds, щоб застарілий DriverStateEvent
    // у SessionService.lastDriverState() не відновив активну мийку при
    // повторному відкритті wash-page після успішного завершення.
    this._cancelledIds.add(washingId);

    // Сервер після FinishVehicleWashing самостійно видаляє сесію та всі фото.
    // Очищаємо лише клієнтський кеш — DeleteImage/DeleteSession повернуть access_denied.
    this._carPhotoImageId.set(null);
    this._displayPhotoImageId.set(null);
    this._finishReceiptImageId.set(null);
    this._finishCarImageId.set(null);
    this._finishInteriorImageId.set(null);
    this.sessionService.clearSessionCacheByType(SessionType.StartVehicleWashing);

    this.stopTimer();
    this.resetState();
  }

  resetState(): void {
    this.stopTimer();
    this._step.set('form');
    this._addressName.set('');
    this._selectedType.set(WashingType.Stationary);
    this._activeRequestId.set(null);
    this._washingId.set(null);
    this._washingType.set(null);
    this._cancelRequestId.set(null);
    this._elapsedSeconds.set(0);

    // Видаляємо фото якщо воно ще є (наприклад, форма кинута без відправки запиту).
    // При cancel — photoId вже null (очищено в cancelRequest), тому нічого не надсилається.
    const photoId = this._carPhotoImageId();
    this._carPhotoImageId.set(null);
    if (photoId) {
      this.imageService.deleteImage(photoId).catch((err: unknown) => {
        console.warn('[Wash] deleteImage failed:', err);
      });
    }

    this._displayPhotoImageId.set(null);

    this._finishReceiptImageId.set(null);
    this._finishCarImageId.set(null);
    this._finishInteriorImageId.set(null);

    // Очищаємо кеш сесії. При cancel — кеш вже очищено через clearSessionCacheByType,
    // тому deleteSessionByType нічого не надсилає. В інших сценаріях (reject, abandon)
    // надсилає DeleteSession на сервер.
    this.sessionService.deleteSessionByType(SessionType.StartVehicleWashing);
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private initFromDriverStateEvent(event: DriverStateEvent): void {
    if (event.vehicleWashing && !this._cancelledIds.has(event.vehicleWashing.id)) {
      this.applyActiveWashing(event.vehicleWashing);
      return;
    }

    const washRequest = event.requests.find(
      (r) => r.type === SessionType.StartVehicleWashing && !this._cancelledIds.has(r.id),
    );
    if (!washRequest) return;

    this._activeRequestId.set(washRequest.id);
    const step = this.mapRequestStatus(washRequest.status);
    this._step.set(step);
    this.restoreWashingTypeFromPayload(washRequest);

    if (step === 'cancel-pending') {
      this.syncCancelRequestId(event.requests, washRequest.id);
    }
  }

  private initFromWorkingShiftData(data: DriverReceiveWorkingShiftEvent): void {
    if (data.vehicleWashing && !this._cancelledIds.has(data.vehicleWashing.id)) {
      this.applyActiveWashing(data.vehicleWashing);
      return;
    }

    const washRequest = data.activeDriverRequests.find(
      (r) => r.type === SessionType.StartVehicleWashing && !this._cancelledIds.has(r.id),
    );
    if (!washRequest) return;

    this._activeRequestId.set(washRequest.id);
    const step = this.mapRequestStatus(washRequest.status);
    this._step.set(step);
    this.restoreWashingTypeFromPayload(washRequest);

    if (step === 'cancel-pending') {
      this.syncCancelRequestId(data.activeDriverRequests, washRequest.id);
    }
  }

  private applyActiveWashing(washing: ActiveVehicleWashing): void {
    this._washingId.set(washing.id);
    this._washingType.set(washing.washingType);
    this._step.set('active');

    const startedAtSec = washing.startedAt
      ? Number(washing.startedAt.seconds)
      : Math.floor(Date.now() / 1000);
    this.startElapsedTimer(startedAtSec);
  }

  private handleDriverStateEvent(event: DriverStateEvent): void {
    if (this._step() === 'form') {
      this.initFromDriverStateEvent(event);
      return;
    }

    // Ресинхронізація таймера при активній мийці
    if (this._step() === 'active' && event.vehicleWashing?.startedAt) {
      const startedAtSec = Number(event.vehicleWashing.startedAt.seconds);
      const elapsed = Math.floor(Date.now() / 1000) - startedAtSec;
      this._elapsedSeconds.set(Math.max(0, elapsed));
    }
  }

  private mapRequestStatus(status: string): WashStep {
    if (status === 'confirmed') return 'approved';
    if (status === 'waiting_cancel') return 'cancel-pending';
    return 'pending';
  }

  private restoreWashingTypeFromPayload(request: ActiveDriverRequest): void {
    if (!request.payload?.length) return;
    try {
      const payload = this.proto.decodeCreateVehicleWashingPayload(request.payload as Uint8Array);
      this._washingType.set(payload.washingType as WashingType);
    } catch { /* ignore decode errors */ }
  }

  private syncCancelRequestId(requests: ActiveDriverRequest[], washRequestId: string): void {
    const cancelRequest = requests.find(
      (r) => r.type === SessionType.CancelDriverRequest && r.payload?.length,
    );
    if (!cancelRequest?.payload) return;
    try {
      const payload = this.proto.decodeCancelDriverRequestPayload(cancelRequest.payload as Uint8Array);
      if (payload.requestId === washRequestId) {
        this._cancelRequestId.set(cancelRequest.id);
      }
    } catch { /* ignore */ }
  }

  private startElapsedTimer(startedAtSeconds: number): void {
    this.stopTimer();
    const update = () => {
      const elapsed = Math.floor(Date.now() / 1000) - startedAtSeconds;
      this._elapsedSeconds.set(Math.max(0, elapsed));
    };
    update();
    this.timerInterval = setInterval(update, 1_000);
  }

  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }
}
